
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteMigrationsRepositoryInterface, { WebsiteMigration } from '@/services/WebsiteMigrations/interfaces';
import { DataTableHeader } from 'vuetify';
import WebsiteRepositoryInterface from '@/services/Website/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteGroupRepositoryInterface, { WebsiteGroup } from '@/services/WebsiteGroup/interfaces';
import { Dictionary } from 'vue-router/types/router';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'websiteMigrationsRepository',
    'websiteRepository',
    'websiteGroupRepository',
    'serverRepository',
  ],
})
export default class GroupsMigrationsList extends Vue {
  public loading: boolean = true;

  public options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public totalMigrationsCount = 10;

  public migrationSearch: string = '';

  public migrations: WebsiteMigration[] = [];
  public websitesDomainMap: { [key: number]: string } = {};

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Source Website', value: 'sourceWebsiteId' },
    { text: 'Target Website', value: 'targetWebsiteId' },
    { text: 'Source Server', value: 'sourceServer' },
    { text: 'Target Server', value: 'targetServer' },
    { text: 'Status', value: 'status' },
  ];

  public websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteGroupRepository!: WebsiteGroupRepositoryInterface;

  public websitesServersMap: { [key: number]: string } = {};
  public serversHostnameMap: { [key: string]: string } = {};

  public async created(): Promise<void> {
    await this.loadMigrations();
  }

  public async loadMigrations(): Promise<void> {
    try {
      const search = this.migrationSearch.toLowerCase();

      let status: string = '';
      if (['done', 'pending', 'error'].includes(search)) {
        status = search;
      }

      const response = await this.websiteMigrationsRepository.getAllMigrations(
        this.options.page,
        this.options.itemsPerPage,
        undefined,
        undefined,
        'migrate-main-to-main',
        status,
      );

      if (!response.totalItems) {
        this.loading = false;
        return;
      }

      const sourceWebsitesIds: number[] = [];
      const targetWebsitesIds: number[] = [];
      response.migrations.forEach((m) => {
        if (!sourceWebsitesIds.includes(m.sourceWebsiteId)) {
          sourceWebsitesIds.push(m.sourceWebsiteId);
        }

        if (!targetWebsitesIds.includes(m.targetWebsiteId)) {
          targetWebsitesIds.push(m.targetWebsiteId);
        }
      });

      const websites = await Promise.all([
        ...(await this.websiteRepository.getWebsitesByIds(sourceWebsitesIds)),
        ...(await this.websiteRepository.getWebsitesByIds(targetWebsitesIds)),
      ]);

      const serversIds: string[] = [];
      websites.forEach((website) => {
        this.websitesDomainMap[website.id] = website.domain;
        this.websitesServersMap[website.id] = website.originServer;

        if (!serversIds.includes(website.originServer)) {
          serversIds.push(website.originServer);
        }
      });

      const servers = await this.serverRepository.getServersByIds(serversIds);
      servers.forEach((server) => {
        if (!this.serversHostnameMap[server._id]) {
          this.serversHostnameMap[server._id] = server.hostname;
        }
      });

      this.totalMigrationsCount = response.totalItems;
      this.migrations = response.migrations;

    } catch (error) {
      this.$errorDialog.show(error.response?.data?.error || error.message);
    } finally {
      this.loading = false;
    }
  }

  public async viewMigration(migration: WebsiteMigration): Promise<void> {
    try {
      const sourceGroup = await this.websiteGroupRepository.getGroupByWebsite(migration.sourceWebsiteId);
      const targetGroup = await this.websiteGroupRepository.getGroupByWebsite(migration.targetWebsiteId);

      if (!sourceGroup && !targetGroup) {
        throw new Error('Group Migration not found');
      }

      const query: Dictionary<string> = {};
      if (sourceGroup) {
        query.sourceGroupId = sourceGroup.id.toString();
      }

      if (targetGroup) {
        query.targetGroupId = targetGroup.id.toString();
      }

      await this.$router.push({
        name: 'group_migrations_list',
        params: { id: sourceGroup?.id || targetGroup.id },
        query,
      });

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
      return;
    }
  }

  public getTypeString(type: string): string {
    return type
      .replaceAll('migrate', '')
      .replaceAll('-', ' ')
      .toUpperCase();
  }
}
